import DehazeIcon from '@mui/icons-material/Dehaze';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { CircularProgress, Fab, Grid, Tooltip } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { AgCheckboxGroupPanel } from '../../common/AgCheckboxGroupPanel';
import { PopperButton } from '../../common/PopperButton';
import { RootState } from '../../store';
import WarehouseSelector from '../stock/WarehouseSelector';
import { getIsProductsLoading, getProductTableVisibleColumnKeys } from './redux/productSelectors';
import {
    deleteProduct,
    fetchProductCategories,
    fetchProductGroups,
    fetchProducts,
    productTableActions,
    showCreateProductDialog,
    warehouseSelected
} from './redux/productSlices';
import ProductTable from './ProductTable';
import { productInitialColDefs } from './productTypes';
import { EntityTypes } from '../../common/PricePrgClient';
import CreateProductDialog from './CreateProductDialog';
import { showConfirmDialog } from '../ui/redux/uiSlices';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
const mapStateToProps = (state: RootState) => ({
    isLoading: getIsProductsLoading(state),
    columnKeys: getProductTableVisibleColumnKeys(state),
    appRestrictions: state.settings.applicationRestrictions,
    isCreateDialogOpen: state.product.ui.createProductDialogVisible,
    selectedProduct: state.product.selectedProduct
});

type Props = ReturnType<typeof mapStateToProps>;

const Product = ({
    isLoading,
    columnKeys,
    appRestrictions,
    isCreateDialogOpen,
    selectedProduct
}: Props) => {
    const dispatch = useDispatch();
    const [selectedWarehouse, setSelectedWarehouse] = useState(null as number | null);
    const canCreateProduct = useMemo(() => {
        return !appRestrictions[EntityTypes.Product]?.isCreateDisabled ?? false;
    }, [appRestrictions]);

    const canDeleteProduct = useMemo(() => {
        return !appRestrictions[EntityTypes.Product]?.isDeleteDisabled ?? false;
    }, [appRestrictions]);

    useEffect(() => {
        dispatch(fetchProductCategories.request());
        dispatch(fetchProductGroups.request());
        dispatch(fetchProducts.request());
    }, [dispatch]);

    const setWarehouse = (value: unknown) => {
        setSelectedWarehouse(value as number);
        dispatch(warehouseSelected(value as number));
    };

    const handleColumnsSelected = (newColumns: string[]) => {
        dispatch(productTableActions.columnsSelected(newColumns));
    };

    const handleCreateProduct = () => {
        dispatch(showCreateProductDialog(true));
    };

    const handleDeleteProduct = () => {
        if (selectedProduct != null) {
            dispatch(
                showConfirmDialog({
                    dialogMessage: 'Do you really want to delete the product?',
                    dialogTitle: 'Delete product',
                    okAction: deleteProduct.request(selectedProduct),
                    primaryActionName: 'Delete'
                })
            );
        }
    };

    return (
        <>
            <Grid
                container
                alignItems="flex-start"
                justifyContent="space-between"
                direction="row"
                wrap="nowrap"
                sx={{ width: '100%' }}
            >
                <WarehouseSelector
                    selectedWarehouse={selectedWarehouse}
                    onSetWarehouse={value => setWarehouse(value)}
                />
                <Grid>
                    <Tooltip sx={{ display: 'inline-flex' }} title="Add product">
                        <Fab
                            sx={{ display: 'inline-flex', marginLeft: 2 }}
                            size="small"
                            color="primary"
                            disabled={!canCreateProduct}
                            onClick={handleCreateProduct}
                        >
                            <AddIcon />
                        </Fab>
                    </Tooltip>
                    <Tooltip sx={{ mx: 2 }} title="Delete product">
                        <Fab
                            size="small"
                            color="error"
                            disabled={!canDeleteProduct}
                            onClick={handleDeleteProduct}
                        >
                            <DeleteIcon />
                        </Fab>
                    </Tooltip>
                </Grid>
                <Grid item>
                    <Grid container spacing={2}>
                        <Grid item xs={6}>
                            <CircularProgress sx={!isLoading ? { visibility: 'hidden' } : undefined} />
                        </Grid>

                        <Grid item xs={6}>
                            <PopperButton btnIcon={<DehazeIcon />} btnTitle="Choose columns">
                                <AgCheckboxGroupPanel
                                    available={productInitialColDefs}
                                    selected={columnKeys}
                                    setSelected={handleColumnsSelected}
                                />
                            </PopperButton>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
            <ProductTable />
            {isCreateDialogOpen && <CreateProductDialog />}
        </>
    );
};

export default connect(mapStateToProps)(Product);
